import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useAuth } from './AuthContext';
import { directOrderService } from '../services/directOrderService';
import { API_ORIGIN } from '../config';
import { useApprovers } from '../utils/approverStore';
import { LEVELS } from '../utils/directOrderStatus';

const DirectOrderContext = createContext(null);

export const useDirectOrder = () => {
  const context = useContext(DirectOrderContext);
  if (!context) {
    throw new Error('useDirectOrder must be used within a DirectOrderProvider');
  }
  return context;
};

export const DirectOrderProvider = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [userDirectOrders, setUserDirectOrders] = useState([]);
  const [allDirectOrders, setAllDirectOrders] = useState([]);
  const [ccDirectOrders, setCcDirectOrders] = useState([]);
  // The signed-in user's orders whose attachments are missing
  const [missingAttachments, setMissingAttachments] = useState([]);
  const [selectedDirectOrder, setSelectedDirectOrder] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isApprover, setIsApprover] = useState(false);
  const [approverLevel, setApproverLevel] = useState(null);
  // The current approval chain (set by Admins on the Approvers page)
  const { approvers } = useApprovers(Boolean(user));

  // Check user permissions on mount or when user changes
  useEffect(() => {
    if (user) {
      const additionalRoles = user.additionalRoles || [];
      const allRoles = [user.role, ...additionalRoles];
      const inChain = approvers.some((a) => a.email === user.email?.toLowerCase());

      if (inChain) {
        setIsAdmin(false);
        setIsApprover(true);
        setApproverLevel(2);
      } else if (allRoles.includes(3)) {
        setIsAdmin(true);
        setIsApprover(true);
        setApproverLevel(3);
      } else if (allRoles.includes(2)) {
        setIsApprover(true);
        setApproverLevel(2);
      } else if (allRoles.includes(1)) {
        setIsApprover(true);
        setApproverLevel(1);
      } else {
        setIsApprover(false);
        setApproverLevel(null);
      }
    } else {
      setIsAdmin(false);
      setIsApprover(false);
      setApproverLevel(null);
    }
  }, [user, approvers]);

  // Load user's direct orders
  const loadUserDirectOrders = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const data = await directOrderService.getUserDirectOrders(user.email);
      setUserDirectOrders(data.orders || []);
    } catch (error) {
      message.error('Failed to load your direct orders');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load all direct orders (admin/approver only)
  const loadAllDirectOrders = useCallback(async () => {
    if (!user || (!isAdmin && !isApprover)) return;

    setLoading(true);
    try {
      const data = await directOrderService.getAllDirectOrders();
      setAllDirectOrders(data.orders || []);
    } catch (error) {
      message.error('Failed to load all direct orders');
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin, isApprover]);

  // Load CC'd direct orders for the current user
  const loadCCDirectOrders = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const data = await directOrderService.getCCDirectOrders(user.email);
      setCcDirectOrders(data.orders || []);
    } catch (error) {
      message.error('Failed to load CC direct orders');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load the user's orders with missing attachments. Failures leave the list empty.
  const loadMissingAttachments = useCallback(async () => {
    if (!user?.email) return;
    try {
      const url = `${API_ORIGIN}/api/direct-order/missing-attachments?userEmail=${encodeURIComponent(user.email)}`;
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setMissingAttachments(data.directOrders || []);
      }
    } catch (error) {
      // ignore: the list simply stays as it was
    }
  }, [user?.email]);

  // Get a specific direct order
  const getDirectOrderById = useCallback(async (doId) => {
    setLoading(true);
    try {
      const data = await directOrderService.getDirectOrderById(doId);
      setSelectedDirectOrder(data.order);
      return data.order;
    } catch (error) {
      message.error('Failed to load direct order details');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new direct order
  const createDirectOrder = useCallback(async (orderData, attachments) => {
    setLoading(true);
    try {
      const result = await directOrderService.createDirectOrder(orderData, attachments);
      loadUserDirectOrders();
      return result;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, [loadUserDirectOrders]);

  // Update direct order status (approve/reject)
  const updateDirectOrderStatus = useCallback(async (doId, action, comments) => {
    if (!isApprover) {
      message.error('You do not have permission to perform this action');
      return null;
    }

    setLoading(true);
    try {
      const order = allDirectOrders.find(o => o.doId === doId);
      if (!order) {
        throw new Error('Order not found');
      }

      // The level to update is the one this user holds in the chain; earlier levels go first.
      let targetApprovalLevel;
      const mine = approvers.find((a) => a.email === user?.email?.toLowerCase());

      if (mine) {
        const isApproved = (value) => [1, 2, 3].includes(Number(value));
        const missing = LEVELS.find((lvl) => lvl.level < mine.level && !isApproved(order[lvl.key]));
        if (missing) {
          throw new Error(`The ${missing.label.toLowerCase()} must be completed first`);
        }
        targetApprovalLevel = LEVELS[mine.level - 1].key;
      } else if (approverLevel === 3 && isAdmin) {
        // Legacy admin logic for backward compatibility
        if (!order.do_firstApproval || order.do_firstApproval === 0) {
          throw new Error('First approval must be completed before admin approval');
        } else if (order.do_firstApproval === 1 && (!order.do_secondApproval || order.do_secondApproval === 0 || order.do_secondApproval === 4)) {
          targetApprovalLevel = 'do_secondApproval';
        } else if (order.do_firstApproval === 1 && order.do_secondApproval === 2 && (!order.do_thirdApproval || order.do_thirdApproval === 0 || order.do_thirdApproval === 4)) {
          targetApprovalLevel = 'do_thirdApproval';
        } else {
          throw new Error('No approval level available for this order');
        }
      } else {
        throw new Error('Invalid approver level');
      }

      const result = await directOrderService.updateDirectOrderStatus(
        doId,
        action,
        targetApprovalLevel,
        comments,
        user
      );

      message.success(`Direct order ${action === 'approve' ? 'approved' : 'rejected'} successfully`);

      if (isAdmin || isApprover) {
        loadAllDirectOrders();
      }

      return result;
    } catch (error) {
      message.error(error.message || `Failed to ${action} direct order`);
      return null;
    } finally {
      setLoading(false);
    }
  }, [approverLevel, approvers, isAdmin, isApprover, loadAllDirectOrders, allDirectOrders, user]);

  // Delete a direct order (admin and approvers only)
  const deleteDirectOrder = useCallback(async (doId) => {
    if (!isAdmin && !isApprover) {
      message.error('You do not have permission to delete direct orders');
      return null;
    }

    setLoading(true);
    try {
      const result = await directOrderService.deleteDirectOrder(doId, user?.email);
      message.success(result.message || 'Direct order deleted successfully');
      loadAllDirectOrders();
      loadUserDirectOrders();
      return result;
    } catch (error) {
      message.error(error.message || 'Failed to delete direct order');
      return null;
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isApprover, loadAllDirectOrders, loadUserDirectOrders]);

  const value = {
    loading,
    userDirectOrders,
    allDirectOrders,
    ccDirectOrders,
    missingAttachments,
    selectedDirectOrder,
    isAdmin,
    isApprover,
    approverLevel,
    user,
    loadUserDirectOrders,
    loadAllDirectOrders,
    loadCCDirectOrders,
    loadMissingAttachments,
    getDirectOrderById,
    createDirectOrder,
    updateDirectOrderStatus,
    deleteDirectOrder
  };

  return (
    <DirectOrderContext.Provider value={value}>
      {children}
    </DirectOrderContext.Provider>
  );
};
